import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

interface ApiAuthConfig {
  getAccessToken: () => Promise<string | null>;
  onSessionExpired?: () => void;
}
declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuth?: boolean;
    identityGeneration?: number;
  }
  interface InternalAxiosRequestConfig {
    skipAuth?: boolean;
    identityGeneration?: number;
  }
}
let authConfig: ApiAuthConfig | null = null;
let generation = 0;
export function invalidateApiRequests() {
  generation++;
}
export function configureApiAuth(config: ApiAuthConfig) {
  authConfig = config;
  invalidateApiRequests();
}
export function clearApiAuth() {
  authConfig = null;
  invalidateApiRequests();
}
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  timeout: 120_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-CE-Scan-Request': '1' },
});
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.identityGeneration = generation;
  if (!config.skipAuth && authConfig) {
    const token = await authConfig.getAccessToken();
    if (config.identityGeneration !== generation)
      throw new axios.CanceledError('Session identity changed');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
api.interceptors.response.use(
  response => {
    if (response.config.identityGeneration !== generation)
      throw new axios.CanceledError('Session identity changed');
    return response;
  },
  (error: AxiosError<{ code?: string }>) => {
    if (error.config?.identityGeneration !== generation)
      return Promise.reject(
        new axios.CanceledError('Session identity changed')
      );
    if (error.response?.data?.code === 'anonymous_session_expired')
      authConfig?.onSessionExpired?.();
    return Promise.reject(error);
  }
);
export default api;
